import { EMPTY_FIELD_PLACEHOLDER } from '@/consts/formatting';
import { AdvisorDto } from '@/models';
import { GlobalSearchPlanDto } from '@/models/GlobalSearchDTO.model';
import { SponsorPlanInfoDto } from '@/models/PlanV2DTO.model';
import {
  PlanFilterType,
  planMainTypesOptions
} from '@/routes/plans/plans/consts';
import AdvisorService from '@/services/Advisor.service';
import { PlanService } from '@/services/Plan.service';
import SponsorService from '@/services/Sponsor.service';
import { Box, Button, Unstable_Grid2 as Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { FC, useEffect } from 'react';

import { PlanAmendmentDetail } from './PlanAmendmentDetail.component';

export const PlanTier = {
  1: 'Workplace',
  2: 'Plus',
  3: 'Pro',
  4: 'Vestwell Flex',
  5: 'Recordkeeping'
};

type SelectedPlanCardProps = {
  selectedPlan: GlobalSearchPlanDto;
  toggle: () => void;
  setPlan: (plan: GlobalSearchPlanDto | null) => void;
  setFieldValue: (field: string, value: any) => void;
};

export const SelectedPlanCard: FC<SelectedPlanCardProps> = props => {
  const planData = useQuery<SponsorPlanInfoDto>(
    ['PlanService.getPlansPage', props.selectedPlan.sponsorPlanId],
    async () =>
      await PlanService.getPlansPage({
        adminStatuses: [],
        filterType: PlanFilterType.PLAN_ID,
        firmIds: null,
        pageNumber: 0,
        pageSize: 25,
        planTypes: planMainTypesOptions,
        pooledPlanId: null,
        recordkeepersIds: [5],
        search: props.selectedPlan.sponsorPlanId.toString(),
        sortBy: 'status',
        sortDirection: 'asc'
      })
  );

  const advisor = useQuery<AdvisorDto>(
    ['AdvisorService.getById', planData.data?.data[0]?.advisorId],
    () => {
      return AdvisorService.getById(planData.data?.data[0]?.advisorId);
    },
    {
      enabled: Boolean(planData.data?.data[0]?.advisorId)
    }
  );

  const sponsorOnboarding = useQuery(
    ['SponsorService.getOnboardingSponsorById', props.selectedPlan.sponsorId],
    () => {
      return SponsorService.getOnboardingSponsorById(
        props.selectedPlan.sponsorId
      );
    },
    {
      enabled: Boolean(props.selectedPlan.sponsorId),
      staleTime: Infinity
    }
  );

  useEffect(() => {
    const authSignerName =
      sponsorOnboarding.data?.fields?.authSignerFirstName ||
      sponsorOnboarding.data?.fields?.authSignerLastName
        ? `${sponsorOnboarding.data?.fields?.authSignerFirstName} ${sponsorOnboarding.data?.fields?.authSignerLastName}`
        : '';

    props.setFieldValue('authorizedSigner', {
      email: sponsorOnboarding.data?.fields?.authorizedSignerEmail || '',
      name: authSignerName,
      phone: sponsorOnboarding.data?.fields?.authSignerWorkPhone || ''
    });

    props.setFieldValue('advisor', {
      email: advisor.data?.data?.attributes?.email || '',
      name: advisor.data?.data?.attributes?.name || '',
      phone: advisor.data?.data?.attributes?.phone || ''
    });

    props.setFieldValue(
      'productTierId',
      sponsorOnboarding.data?.fields?.planTier
    );

    props.setFieldValue(
      'firmId',
      +advisor.data?.data?.relationships?.firm?.data?.id
    );
  }, [sponsorOnboarding.data, advisor.data]);

  return (
    <Box
      border={1}
      borderColor='grey.400'
      borderRadius={1}
      data-testid='selected-plan-card'>
      <Grid container margin={2}>
        <Grid container direction='row' justifyContent='space-between' xs={12}>
          <Grid>
            <Typography fontWeight='bold'>Selected Plan</Typography>
          </Grid>
          <Grid>
            <Button
              data-testid='change-plan-btn'
              onClick={() => {
                props.toggle();
                props.setFieldValue('planId', null);
                props.setPlan(null);
              }}>
              Change Plan
            </Button>
          </Grid>
        </Grid>

        <Grid container direction='column' xs={12}>
          <Grid container direction='column'>
            <PlanAmendmentDetail
              isJPM={
                +advisor.data?.data?.relationships?.firm?.data?.id === 1245
              }
              isOnboarding={props.selectedPlan.adminStatus === 'Onboarding'}
              planName={props.selectedPlan.planName}
              size='large'
              sponsorPlanId={props.selectedPlan.sponsorPlanId}
              updatedFilters={{}}
            />
          </Grid>

          <Grid container direction='row' paddingTop={2}>
            <Grid xs={6}>
              <Typography color='grey.600'>Authorized Signer</Typography>
              <Typography paddingY={0.5}>
                {sponsorOnboarding.data?.fields?.authSignerFirstName &&
                sponsorOnboarding.data?.fields?.authSignerLastName
                  ? `${sponsorOnboarding.data?.fields?.authSignerFirstName} ${sponsorOnboarding.data?.fields?.authSignerLastName}`
                  : EMPTY_FIELD_PLACEHOLDER}
              </Typography>
              <Typography color='grey.600' fontSize={13}>
                {sponsorOnboarding.data?.fields?.authorizedSignerEmail ||
                  EMPTY_FIELD_PLACEHOLDER}
              </Typography>
              <Typography color='grey.600' fontSize={13}>
                {sponsorOnboarding.data?.fields?.authSignerWorkPhone ||
                  EMPTY_FIELD_PLACEHOLDER}
              </Typography>
            </Grid>

            <Grid xs={6}>
              <Typography color='grey.600'>Advisor</Typography>
              <Typography paddingY={0.5}>
                {advisor.data?.data?.attributes?.name ||
                  EMPTY_FIELD_PLACEHOLDER}
              </Typography>
              <Typography color='grey.600' fontSize={13}>
                {advisor.data?.data?.attributes?.email ||
                  EMPTY_FIELD_PLACEHOLDER}
              </Typography>
              <Typography color='grey.600' fontSize={13}>
                {advisor.data?.data?.attributes?.phone ||
                  EMPTY_FIELD_PLACEHOLDER}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
